import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { environment } from '../../../environments/environment';
import { UserService } from '../../../services/user.service';
import { TrendingHomePostDto, UserPostsDataService } from '../../../services/posts.service';
import { RegistrationComponent } from '../../../modals/sign_up.modal/sign_up.modal.component';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: unknown) => void;
          renderButton: (el: HTMLElement, opts: unknown) => void;
        };
      };
    };
  }
}

@Component({
  selector: 'app-public-home',
  templateUrl: './public-home.component.html',
  styleUrls: ['./public-home.component.css'],
})
export class PublicHomeComponent implements OnInit, AfterViewInit {
  readonly environment = environment;

  email = '';
  password = '';
  errorMessage = '';
  returnUrl = '/dashboard';
  trendingCards: Array<{
    displayName: string;
    handle: string;
    avatarUrl: string;
    content: string;
    likes: number;
    reposts: number;
    comments: number;
    tag: string;
    delayMs: number;
  }> = [];
  trendingLoadError = '';
  googleReady = false;

  constructor(
    private readonly authService: UserService,
    private readonly postsService: UserPostsDataService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    this.postsService.getTrendingHomePosts().subscribe({
      next: (rows) => {
        const slice = (rows || []).slice(0, 5);
        this.trendingCards = slice.map((row, index) => this.mapTrendingRow(row, index));
      },
      error: () => {
        this.trendingLoadError = 'Trending posts could not be loaded.';
      },
    });
  }

  ngAfterViewInit(): void {
    this.scheduleGoogleInit();
  }

  private scheduleGoogleInit(attempt = 0): void {
    if (!environment.googleSignInEnabled || !environment.googleClientId) {
      return;
    }
    if (typeof window === 'undefined' || !window.google?.accounts?.id) {
      if (attempt < 80) {
        setTimeout(() => this.scheduleGoogleInit(attempt + 1), 100);
      }
      return;
    }
    window.google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (resp: { credential: string }) => this.onGoogleCredential(resp),
      auto_select: false,
    });
    const host = document.getElementById('googleSignInButton');
    if (host) {
      window.google.accounts.id.renderButton(host, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'signin_with',
      });
      // Defer so *ngIf="!googleReady" does not flip in the same change-detection pass (NG0100).
      setTimeout(() => {
        this.googleReady = true;
      }, 0);
    }
  }

  onGoogleCredential(resp: { credential: string }): void {
    this.errorMessage = '';
    if (!resp?.credential) {
      this.errorMessage = 'Google did not return a credential.';
      return;
    }
    this.authService.googleLogin({ idToken: resp.credential }).subscribe({
      next: (r: Record<string, unknown>) => {
        const needs =
          r?.['needsUsername'] === true ||
          r?.['NeedsUsername'] === true;
        if (needs) {
          const email = (r['email'] ?? r['Email'] ?? '') as string;
          this.router.navigate(['/sign_in'], {
            queryParams: { google: '1', email },
          });
          return;
        }
        this.router.navigateByUrl(this.returnUrl);
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Google sign-in failed.';
        this.errorMessage = typeof msg === 'string' ? msg : 'Google sign-in failed.';
      },
    });
  }

  login(): void {
    this.errorMessage = '';
    this.authService.login(this.email, this.password).subscribe({
      next: () => this.router.navigateByUrl(this.returnUrl),
      error: () => {
        this.errorMessage = 'The username or password entered is not correct.';
      },
    });
  }

  openCreateAccount(): void {
    const ref: NgbModalRef = this.modalService.open(RegistrationComponent, {
      keyboard: false,
      backdrop: 'static',
      size: 'lg',
    });
    void ref;
  }

  private mapTrendingRow(row: TrendingHomePostDto, index: number) {
    const display =
      row.profileName ?? row.ProfileName ?? row.userName ?? row.UserName ?? '';
    const un = row.userName ?? row.UserName ?? '';
    const handle = un ? '@' + un : '';
    return {
      displayName: display,
      handle,
      avatarUrl: row.userImg ?? row.UserImg ?? '',
      content: row.postContent ?? row.PostContent ?? '',
      likes: row.likes ?? row.Likes ?? 0,
      reposts: row.reposts ?? row.Reposts ?? 0,
      comments: row.comments ?? row.Comments ?? 0,
      tag: row.categoryTag ?? row.CategoryTag ?? '',
      delayMs: index * 60,
    };
  }
}
